import React, { useState, useEffect } from 'react';
import { Button, Form, Input, Alert, Card, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { passwordRules, PASSWORD_REQUIREMENTS } from './passwordValidation';

const { Title, Text } = Typography;

interface ActivateFormProps {
  onSubmit?: (values: {
    token: string;
    password: string;
    firstName?: string;
    lastName?: string;
    stateToken?: string;
  }) => Promise<void>;
  onValidateToken?: (token: string) => Promise<{
    isValid: boolean;
    email?: string;
    userId?: string;
    error?: string;
    stateToken?: string;
  }>;
  loading?: boolean;
  validatingToken?: boolean;
  error?: string;
  success?: string;
}

export default function ActivateForm({
  onSubmit,
  onValidateToken,
  loading = false,
  validatingToken = false,
  error,
  success,
}: ActivateFormProps) {
  const [form] = Form.useForm();
  const { token } = useParams<{ token: string }>();
  const [tokenData, setTokenData] = useState<{
    isValid: boolean;
    email?: string;
    userId?: string;
    error?: string;
    stateToken?: string;
  } | null>(null);

  useEffect(() => {
    if (token && onValidateToken && !tokenData) {
      onValidateToken(token).then((params) => {
        setTokenData(params);
      });
    }
  }, [token, onValidateToken, tokenData]);

  const handleSubmit = async (values: {
    password: string;
    confirmPassword: string;
    firstName?: string;
    lastName?: string;
  }) => {
    if (!token) return;
    if (onSubmit) {
      await onSubmit({
        token,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        stateToken: tokenData?.stateToken,
      });
    }
  };

  // Show loading state while validating token
  if (validatingToken || (!tokenData && token)) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Card style={{ width: '100%', maxWidth: 400, padding: '2rem' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3}>Validating activation link...</Title>
          </div>
        </Card>
      </div>
    );
  }

  // Show error if token is invalid
  if (tokenData && !tokenData.isValid) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Card style={{ width: '100%', maxWidth: 400, padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <Title level={3} type="danger">
              Invalid Activation Link
            </Title>
          </div>

          <Alert
            message="Activation Link Error"
            description={tokenData.error || 'This activation link is invalid or has expired.'}
            type="error"
            style={{ marginBottom: '2rem' }}
            showIcon
          />

          <div style={{ textAlign: 'center' }}>
            <Link to="/auth/regenerate">
              <Button
                type="primary"
                style={{
                  backgroundColor: '#93c90e',
                  borderColor: '#93c90e',
                  marginRight: '1rem',
                }}
              >
                Request New Link
              </Button>
            </Link>
            <Link to="/login">
              <Button>Back to Login</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 450,
          padding: '2rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title level={2} style={{ color: '#93c90e', marginBottom: '0.5rem' }}>
            Activate Your Account
          </Title>
          {tokenData?.email && (
            <Text type="secondary">Complete your registration for {tokenData.email}</Text>
          )}
        </div>

        {error && (
          <Alert
            message="Activation Error"
            description={error}
            type="error"
            style={{ marginBottom: '1rem' }}
            showIcon
          />
        )}

        {success && (
          <Alert
            message="Account Activated!"
            description={`${success} Redirecting...`}
            type="success"
            style={{ marginBottom: '1rem' }}
            showIcon
          />
        )}

        <Form form={form} layout="vertical" onFinish={handleSubmit} size="large">
          <Form.Item name="password" label="Password" rules={passwordRules}>
            <Input.Password
              placeholder="Create a password"
              autoComplete="new-password"
              style={{ borderRadius: '6px' }}
            />
          </Form.Item>

          <div style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
            <Text type="secondary" style={{ fontSize: '0.875rem' }}>
              Password requirements:
            </Text>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
              {PASSWORD_REQUIREMENTS.map((req) => (
                <li key={req}>{req}</li>
              ))}
            </ul>
          </div>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Confirm your password"
              autoComplete="new-password"
              style={{ borderRadius: '6px' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: '1rem' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                width: '100%',
                height: '45px',
                borderRadius: '6px',
                backgroundColor: '#93c90e',
                borderColor: '#93c90e',
              }}
            >
              {loading ? 'Activating...' : 'Activate Account'}
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">
              Already have an active account?{' '}
              <Link to="/login" style={{ color: '#93c90e' }}>
                Sign In
              </Link>
            </Text>
          </div>
        </Form>
      </Card>
    </div>
  );
}
